import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import {
  ssoToken, adminInquiries, adminMarkInquiryRead, adminSubmissions, adminSubmission, adminSetSubmissionStatus, adminImportSubmission, errorMessage,
} from "./api";
import { CredentialsModal } from "./ui";

const STATUSES = ["new", "reviewed", "imported"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "");
const th = "text-left text-xs uppercase tracking-wider text-muted-foreground px-3 py-2";
const td = "px-3 py-2 text-sm border-t border-sand-300";

// Admin review inbox. Read-only over public input: an admin imports schools,
// sections and learners by hand — nothing here (or in the public forms) does it.
// `embedded` (+ the dashboard's `token`): shown inside the dashboard, sidebar and all; standalone at /admin otherwise.
export default function AdminInbox({ embedded = false, token: givenToken = null }) {
  const [token, setToken] = useState(givenToken);
  const [state, setState] = useState("loading"); // loading | signin | forbidden | ready
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [unread, setUnread] = useState(0);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(null); // full submission being reviewed
  const [creds, setCreds] = useState(null);

  const load = useCallback(async (t) => {
    try {
      const [inq, sub] = await Promise.all([adminInquiries(t), adminSubmissions(t)]);
      setInquiries(inq.inquiries); setUnread(inq.unread); setSubs(sub); setState("ready");
    } catch (err) {
      setState(err?.response?.status === 403 ? "forbidden" : "signin");
    }
  }, []);

  useEffect(() => {
    if (embedded && givenToken) { setToken(givenToken); load(givenToken); return; }
    ssoToken().then((t) => { setToken(t); load(t); }).catch(() => setState("signin"));
  }, [load, embedded, givenToken]);

  const markRead = async (id) => {
    try { await adminMarkInquiryRead(token, id); load(token); } catch (err) { toast.error(errorMessage(err)); }
  };
  const openSub = async (id) => {
    try { setOpen(await adminSubmission(token, id)); } catch (err) { toast.error(errorMessage(err)); }
  };
  const setStatus = async (id, status) => {
    try { await adminSetSubmissionStatus(token, id, status); setOpen((o) => ({ ...o, status })); load(token); } catch (err) { toast.error(errorMessage(err)); }
  };

  const doImport = async () => {
    if (!window.confirm(`Create real accounts for ${open.teachers.length} teachers and ${open.students.length} students at ${open.school_name}? This can't be undone.`)) return;
    try {
      const r = await adminImportSubmission(token, open.submission_id);
      setCreds([...r.teachers.map((t) => ({ ...t, class_name: "Teacher" })), ...r.students]);
      if (r.skipped_teachers.length) toast.warning(`Skipped: ${r.skipped_teachers.map((x) => `${x.email} (${x.reason})`).join("; ")}`);
      setOpen((o) => ({ ...o, status: "imported" })); load(token);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  if (state === "loading") return <div className={embedded ? "py-16 text-center text-forest-700 font-display" : "min-h-screen geo-bg grid place-items-center text-forest-700 font-display"}>Loading…</div>;
  if (state !== "ready") {
    return (
      <div className={embedded ? "py-16 text-center" : "min-h-screen geo-bg grid place-items-center p-6 text-center"} data-testid="admin-blocked">
        <div>
          <h1 className="font-display font-semibold text-2xl text-forest-700">{state === "forbidden" ? "Admins only" : "Please sign in"}</h1>
          <p className="mt-2 text-foreground/70">{state === "forbidden" ? "This account doesn't have admin access." : "Sign in with an admin account first."}</p>
          <Link to="/?switch=1" className="mt-4 inline-block text-forest-700 font-semibold hover:underline">Go to sign in</Link>
          {state === "forbidden" && <div><Link to="/dashboard" className="mt-2 inline-block text-sm text-forest-700 hover:underline">Back to dashboard</Link></div>}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "min-h-screen geo-bg p-4 md:p-10"} data-testid="admin-inbox">
      <div className={embedded ? "" : "max-w-5xl mx-auto"}>
        {!embedded && <Link to="/dashboard" data-testid="inbox-back" className="inline-flex items-center gap-1 text-sm text-forest-700 hover:underline mb-3"><ArrowLeft className="h-4 w-4" />Back to dashboard</Link>}
        <h1 className="font-display font-semibold text-3xl text-forest-700">Inbox</h1>
        <div className="mt-5 flex gap-2">
          {[["inquiries", `Inquiries${unread ? ` (${unread} unread)` : ""}`], ["onboarding", "School Onboarding"]].map(([k, label]) => (
            <button key={k} type="button" data-testid={`tab-${k}`} onClick={() => { setTab(k); setOpen(null); }}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-forest-700 text-sand-100" : "bg-white border border-sand-300 text-forest-700"}`}>{label}</button>
          ))}
        </div>

        {tab === "inquiries" && (
          <div className="mt-5 overflow-x-auto rounded-2xl border border-sand-300 bg-white">
            <table className="w-full" data-testid="inquiries-table">
              <thead><tr><th className={th}>School</th><th className={th}>Contact</th><th className={th}>Location</th><th className={th}>Students</th><th className={th}>Received</th><th className={th} /></tr></thead>
              <tbody>
                {inquiries.map((q) => (
                  <tr key={q.inquiry_id} className={q.read ? "" : "font-semibold bg-gold-500/5"}>
                    <td className={td}>{q.school_name}<div className="text-xs font-normal text-foreground/60">{q.address}</div></td>
                    <td className={td}>{q.contact_email}<div className="text-xs font-normal text-foreground/60">{q.contact_phone}{q.contact_name ? ` · ${q.contact_name}` : ""}</div></td>
                    <td className={td}>{q.city_country}</td>
                    <td className={td}>{q.student_count}</td>
                    <td className={td}>{fmt(q.created_at)}</td>
                    <td className={td}>{!q.read && <button type="button" className="text-forest-700 hover:underline" onClick={() => markRead(q.inquiry_id)}>Mark read</button>}</td>
                  </tr>
                ))}
                {!inquiries.length && <tr><td className={td} colSpan={6}>No inquiries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "onboarding" && !open && (
          <div className="mt-5 overflow-x-auto rounded-2xl border border-sand-300 bg-white">
            <table className="w-full" data-testid="submissions-table">
              <thead><tr><th className={th}>School</th><th className={th}>Principal</th><th className={th}>Teachers</th><th className={th}>Students (roster / stated)</th><th className={th}>Status</th><th className={th}>Received</th></tr></thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.submission_id} className="cursor-pointer hover:bg-sand-100" onClick={() => openSub(s.submission_id)}>
                    <td className={td}>{s.school_name}</td>
                    <td className={td}>{s.principal_name}<div className="text-xs text-foreground/60">{s.principal_email}</div></td>
                    <td className={td}>{s.teacher_count}</td>
                    <td className={td}>{s.student_count_roster} / {s.student_count}{s.student_count_roster !== s.student_count && <span className="ml-2 text-xs text-amber-700">check</span>}</td>
                    <td className={td}>{s.status}</td>
                    <td className={td}>{fmt(s.created_at)}</td>
                  </tr>
                ))}
                {!subs.length && <tr><td className={td} colSpan={6}>No submissions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "onboarding" && open && (
          <div className="mt-5 space-y-6" data-testid="submission-detail">
            <button type="button" className="text-sm text-forest-700 hover:underline" onClick={() => setOpen(null)}>← All submissions</button>
            <div className="rounded-2xl border border-sand-300 bg-white p-5">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <h2 className="font-display font-semibold text-xl text-forest-700">{open.school_name}</h2>
                {open.status !== "imported" && <button type="button" data-testid="import-submission" className="btn-primary" onClick={doImport}>Import &amp; create accounts</button>}
                <label className="text-sm">Status{" "}
                  <select data-testid="status-select" className="ml-1 rounded-lg border border-sand-300 px-2 py-1" value={open.status} onChange={(e) => setStatus(open.submission_id, e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
              <p className="mt-2 text-sm text-foreground/70">{open.address}, {open.city_country} · {open.contact_email} · {open.contact_phone}</p>
              <p className="text-sm text-foreground/70">Principal: {open.principal_name} ({open.principal_email})</p>
              <p className="text-sm text-foreground/70">Stated students: {open.student_count} · In roster: {open.students.length}
                {open.students.length !== open.student_count && <span className="ml-2 text-amber-700">— counts differ, flag for review (neither is the source of truth)</span>}</p>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
              <div className="px-4 pt-3 font-display font-semibold text-forest-700">Teachers ({open.teachers.length})</div>
              <table className="w-full"><thead><tr><th className={th}>Name</th><th className={th}>Email</th><th className={th}>Class</th><th className={th}>Section</th></tr></thead>
                <tbody>{open.teachers.map((t, i) => <tr key={i}><td className={td}>{t.name}</td><td className={td}>{t.email}</td><td className={td}>{t.class_name}</td><td className={td}>{t.section}</td></tr>)}</tbody></table>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
              <div className="px-4 pt-3 font-display font-semibold text-forest-700">Students ({open.students.length})</div>
              <table className="w-full"><thead><tr><th className={th}>Name</th><th className={th}>Class</th><th className={th}>Section</th><th className={th}>Age</th><th className={th}>Gender</th><th className={th}>Parent mobile</th></tr></thead>
                <tbody>{open.students.map((s, i) => <tr key={i}><td className={td}>{s.name}</td><td className={td}>{s.class_name}</td><td className={td}>{s.section}</td><td className={td}>{s.age}</td><td className={td}>{s.gender}</td><td className={td}>{s.parent_mobile || "—"}</td></tr>)}</tbody></table>
            </div>
          </div>
        )}
      </div>
      {creds && <CredentialsModal title="Accounts created — hand these to the school" rows={creds} onClose={() => setCreds(null)} />}
    </div>
  );
}
